# Introduction

The SEEK API provides access to SEEK’s employment marketplace.
This documentation is targeted at developers integrating their recruitment software with SEEK.

## Integration process

SEEK uses a seven stage process for developing integrations with the SEEK API to ensure a high quality experience for our mutual hirers:

1. **Review the documentation**

   Read through the documentation available on this website, and the [SEEK API Terms of Use],
   to understand the concepts of the SEEK API and the scope of integrating it with your software.

2. **Request to integrate**

   Fill in our [integration request form], or reach out to your dedicated Partner Manager, to get started.
   We’ll be in contact to guide you through the process and set up your access to the [Developer Dashboard].

3. **Build and test with the Playground**

   Once you can access the Developer Dashboard, provision [Playground] credentials on the [Developer Dashboard credentials page].
   These credentials grant you limited permissions to the SEEK API’s read-only mock data;
   use them to test accessing the SEEK API and mapping its data structures.
   While you can test the input validation of mutations with these credentials,
   the SEEK API will ultimately forbid requests to mutate our read-only mock data.

   The Developer Dashboard also provides a view of your live [hirer relationships] and [events].
   This will give you visibility on your production SEEK API access before requesting your partner credentials.

4. **Request live credentials**

   Once you’ve reached the limits of what you can do with the Playground, contact SEEK to request access to manage live [partner credentials].

   Once approved, provision your live credentials using the [Developer Dashboard credentials page].
   Live credentials grant you access to live production data.

   At the same time, SEEK will add a relationship between your partner account and an unsearchable SEEK test hirer.
   Test hirers can export candidate applications and post job ads free of charge, but their job ads don’t appear in SEEK’s search results.
   This should be sufficient to test your software end-to-end against the SEEK API.

5. **Test your integration**

   SEEK may share a test plan with you to ensure your integration meets our quality standards.
   In addition, we may require access to your sandbox/test environment as applicable, to do our own validation.
   This is an important milestone for your integration,
   ensuring it meets user experience expectations and appropriately represents SEEK’s value proposition.

6. **Release your integration**

   When you’re ready to go live, contact SEEK to discuss your release plans.
   For initial integrations, SEEK will work with you to ensure a smooth transition for you and our mutual hirers.
   In some cases, this may involve a limited pilot release to a subset of your customers.

   For ongoing changes to your integration, keep SEEK informed of your release plans so that we can communicate accurate information to our mutual hirers during Sales and Support interactions.

7. **Maintain your integration**

   Subscribe to our [release notes] to stay informed of changes as they reach
   general availability and evolve your integration accordingly.

   Review our [Monitoring and troubleshooting] documentation to understand how to monitor your integration’s performance and troubleshoot issues, including use of the [Developer Dashboard].
   [Contact SEEK’s support team] for ongoing technical support.

We recommend to repeat these steps for any new development, not just for your initial integration.
In particular, continue to use your [Playground] credentials for exploratory testing, as your partner credentials are highly sensitive.

[Developer Dashboard credentials page]: /manage/credentials
[Developer Dashboard]: /manage
[events]: /events
[hirer relationships]: /auth/hirer-relationships
[integration request form]: https://employer.seek.com/partners/integration-request/
[monitoring and troubleshooting]: /guides/monitoring-and-troubleshooting
[partner credentials]: /auth#client-credentials
[playground]: /graphql/playground
[release notes]: /introduction/release-notes
[seek api terms of use]: https://au.employer.seek.com/partners/terms-of-use
[contact seek’s support team]: https://au.employer.seek.com/contactus

## Technology

The SEEK API leverages existing industry standards whenever possible.
The core of the API is built on:

import { Tiles } from 'braid-design-system';
import { TileLink } from '../components/TileLink/TileLink';
import { PageIcon } from '../components/PageIcon/PageIcon';

<Tiles columns={[1, 1, 2]} space="small">
  <TileLink
    description="Securely connect to SEEK with OAuth 2.0 flows"
    href="/auth"
    icon={<PageIcon icon="Auth" />}
    title="Auth"
  />
  <TileLink
    description="Access our API endpoint and follow best practices"
    href="/graphql"
    icon={<PageIcon icon="GraphQL" />}
    title="GraphQL"
  />
  <TileLink
    description="Embed pre-built components to reduce integration effort"
    href="/introduction/web-components"
    icon={<PageIcon icon="Panel" />}
    title="Web components"
  />
  <TileLink
    description="Be notified when an action occurs on our marketplace with our webhooks"
    href="/events"
    icon={<PageIcon icon="Webhook" />}
    title="Events"
  />
</Tiles>

## Multi-market support

The SEEK API is enabled across Asia-Pacific (APAC) markets in the [SEEK Group]:

| Code | Name            | Brand       |
| :--: | :-------------- | :---------- |
|  AU  | Australia       | [SEEK]      |
|  HK  | Hong Kong       | [Jobsdb]    |
|  ID  | Indonesia       | [Jobstreet] |
|  MY  | Malaysia        | Jobstreet   |
|  NZ  | New Zealand     | [SEEK]      |
|  PH  | The Philippines | Jobstreet   |
|  SG  | Singapore       | Jobstreet   |
|  TH  | Thailand        | Jobsdb      |

Our documentation provides guidance on regional features.

[Jobsdb]: https://www.jobsdb.com/
[Jobstreet]: https://www.jobstreet.com/
[SEEK]: https://www.seek.com/
[SEEK Group]: https://au.seek.com/about
